/**
 * System Burden Diagnostic Page
 *
 * Dependency-based diagnostic using 14 organizational dependency measures
 * to assess system burden and calculate value recovery potential.
 *
 * Features:
 * - Persona selection (Mark Ellison or Sarah Lawson)
 * - Value at stake estimation
 * - 14 dependency capability assessments (0-10 scale)
 * - Calculated DCF from dependency scores
 * - Combined Recovery Factor (CRF) with persona baseline LPFF
 * - McKinsey evidence-based constants
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PERSONA_BASELINES,
  calculateSystemBurdenV2,
  DEPENDENCY_WEIGHTS
} from '../../utils/algorithms';
import DiagnosticHeader from '../../components/diagnostics/DiagnosticHeader';
import '../../styles/diagnostics/index.css';

function SystemBurdenDiagnostic() {
  const { code } = useParams(); // GUID from URL
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentStep, setCurrentStep] = useState(1); // 1=persona, 2=value, 3=dependencies

  const [assessmentData, setAssessmentData] = useState({
    code: code,
    personaId: 'mark', // Default to Mark
    valueAtStake: '',
    scores: {
      dep1: 5, dep2: 5, dep3: 5, dep4: 5, dep5: 5, dep6: 5, dep7: 5,
      dep8: 5, dep9: 5, dep10: 5, dep11: 5, dep12: 5, dep13: 5, dep14: 5
    }
  });

  // 14 organizational dependencies
  // eslint-disable-next-line no-unused-vars
  const dependencies = [
    { id: 1, label: 'Strategic clarity and value case quality', description: 'Clear transformation goals, compelling business case, measurable outcomes' },
    { id: 2, label: 'Portfolio governance and prioritisation', description: 'Decision rights clarity, effective prioritization, governance that enables not blocks' },
    { id: 3, label: 'Risk planning and mitigation', description: 'Living risk intelligence, adaptive planning, ability to pivot without freezing' },
    { id: 4, label: 'Delivery flow and execution rhythm', description: 'Work prioritization, dependency mapping, bottleneck resolution' },
    { id: 5, label: 'Performance management and forecasting', description: 'Trustworthy data, timely metrics, reliable forecasting for course correction' },
    { id: 6, label: 'Change implementation capability', description: 'BA/DM skills, change translation capability, turning plans into executed change' },
    { id: 7, label: 'Active sponsorship and stakeholder alignment', description: 'Engaged sponsors, aligned stakeholder groups, sustained executive commitment' },
    { id: 8, label: 'Communication and expectation management', description: 'Clear messaging, managed expectations, psychological safety to surface issues' },
    { id: 9, label: 'Team capability and role stability', description: 'Right skills, stable teams, manageable workload, clear role definitions' },
    { id: 10, label: 'Organisational complexity and scale', description: 'Manageable change scale, bounded scope, realistic ambition relative to capability' },
    { id: 11, label: 'Delivery methodology and tooling', description: 'Fit-for-purpose methods, enabling tools not bureaucracy, pragmatic delivery approach' },
    { id: 12, label: 'Cultural readiness and trust', description: 'Change-friendly culture, psychological safety, trust in leadership and each other' },
    { id: 13, label: 'External dependencies and constraints', description: 'Managed external dependencies, regulatory clarity, vendor/partner reliability' },
    { id: 14, label: 'Resource availability and flexibility', description: 'Adequate budget, available talent, ability to flex resources as needs evolve' }
  ];

  // Validate GUID and check if already completed on mount
  useEffect(() => {
    const checkAssessmentStatus = async () => {
      if (!code) {
        setError('No assessment code provided');
        setLoading(false);
        return;
      }

      // Basic GUID validation
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidPattern.test(code)) {
        setError('Invalid assessment code format');
        setLoading(false);
        return;
      }

      // Check if assessment is already completed
      try {
        const response = await fetch(`/api/get-system-burden-results?code=${code}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.results && data.results.completedAt) {
            // Assessment is completed, redirect to results
            navigate(`/diagnostics/system-burden/results/${code}`);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking assessment status:', error);
        // Continue anyway - if check fails, let them try the assessment
      }

      setLoading(false);
    };

    checkAssessmentStatus();
  }, [code, navigate]);

  // Step 1: Persona Selection Handler
  const handlePersonaSelect = (personaId) => {
    setAssessmentData(prev => ({ ...prev, personaId }));
    // Don't auto-advance - wait for Continue button
  };

  // Step 2: Value Input Handler
  const handleValueChange = (e) => {
    let value = e.target.value;
    setAssessmentData(prev => ({ ...prev, valueAtStake: value }));
  };

  const handleValueContinue = () => {
    const value = parseFloat(assessmentData.valueAtStake);
    if (value >= 0.1 && value <= 1000) {
      setCurrentStep(3);
      window.scrollTo(0, 0);
    }
  };

  // Step 3: Dependency Slider Handler
  const handleScoreChange = (depId, value) => {
    setAssessmentData(prev => ({
      ...prev,
      scores: {
        ...prev.scores,
        [depId]: parseInt(value)
      }
    }));
  };

  // Calculate DCF in real-time using weighted formula
  const calculateDCF = () => {
    let totalWeightedContribution = 0;
    let totalWeight = 0;
    DEPENDENCY_WEIGHTS.forEach(dep => {
      const score = assessmentData.scores[`dep${dep.id}`] || 0;
      totalWeightedContribution += dep.weight * score;
      totalWeight += dep.weight;
    });
    const dcf = totalWeightedContribution / (totalWeight * 10); // divisor = 1130
    return dcf;
  };

  // Determine zone from DCF (aligned with algorithms.js thresholds)
  const determineZone = (dcf) => {
    if (dcf < 0.70) return 'Foundational';
    if (dcf < 0.85) return 'Stretch';
    return 'Mastery';
  };

  // Check if all sliders have been interacted with
  // eslint-disable-next-line no-unused-vars
  const allSlidersCompleted = () => {
    return Object.keys(assessmentData.scores).length === 14;
  };

  // Submit handler
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate all metrics using algorithms v7.0
      const valueAtStake = parseFloat(assessmentData.valueAtStake);
      const calculations = calculateSystemBurdenV2(
        valueAtStake,
        assessmentData.scores,
        assessmentData.personaId
      );

      // Prepare payload for API (v2.0 format)
      const payload = {
        code: assessmentData.code,
        personaId: assessmentData.personaId,
        valueAtStake: valueAtStake,
        scores: assessmentData.scores,  // Send as object: { dep1: 5, dep2: 6, ... }
        calculations: {
          totalValueLoss: calculations.totalLoss,
          systemLoss: calculations.systemLoss,
          leadershipLoss: calculations.leadershipLoss,
          dcf: calculations.dcf,
          lpffBaseline: calculations.lpffBaseline,
          crf: calculations.crf,
          recoverableValue: calculations.recoverableValue,
          recoveryPoints: calculations.recoveryPoints,
          startingCaptureRateAverage: 0.57,  // McKinsey constant
          finalCaptureRate: calculations.finalCaptureRate,
          maximumRecoveryRate: 0.233,
          mckinseySuccessfulCeiling: calculations.mckinseySuccessfulCeiling
        }
      };

      // POST to API
      const response = await fetch('/api/save-system-burden-results', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to save results');
      }

      // Redirect to results page
      navigate(`/diagnostics/system-burden/results/${assessmentData.code}`);

    } catch (err) {
      console.error('Submission error:', err);
      setError(err.message || 'Failed to submit assessment. Please try again.');
      setLoading(false);
    }
  };

  // Loading state
  if (loading && currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 16px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div className="card">
            <p style={{ textAlign: 'center', color: '#697A90' }}>Loading assessment...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && currentStep === 1) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', padding: '48px 16px' }}>
        <div style={{ maxWidth: '1024px', margin: '0 auto' }}>
          <div className="card">
            <h1 style={{ color: '#ef4444' }}>Assessment Error</h1>
            <p style={{ color: '#697A90', marginTop: '16px' }}>{error}</p>
            <p style={{ color: '#697A90', marginTop: '8px', fontSize: '14px' }}>
              Please check your assessment link and try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const persona = PERSONA_BASELINES[assessmentData.personaId];
  const currentDCF = calculateDCF();
  // eslint-disable-next-line no-unused-vars
  const currentZone = determineZone(currentDCF);

  // ============================================
  // STEP 1: Persona Selection
  // ============================================
  if (currentStep === 1) {
    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 1 OF 4</div>
          <h1>System Burden Diagnostic</h1>
          <p className="subtitle">
            Understand how organizational system dependencies impact your transformation success
          </p>

          <div className="info-box blue">
            <p><strong>What This Assessment Does:</strong></p>
            <p>This diagnostic measures organizational system dependencies that impact transformation outcomes. It assesses 14 critical dependencies using capability-based scoring to calculate your Dependency Capability Factor (DCF) and estimate transformation value at risk.</p>
            <p style={{ marginTop: '12px' }}><strong>Important:</strong> This assessment is organizational—not personal. It focuses on structural and systemic issues, not individual leadership anxieties.</p>
          </div>
        </div>

        {/* What is System Burden */}
        <div className="card">
          <h2>What is System Burden?</h2>
          <p>
            System Burden refers to the structural, procedural, and organizational barriers that slow down or prevent transformation work from succeeding. Unlike leadership anxieties (which are psychological), system burdens are tangible constraints embedded in how your organization operates.
          </p>

          <div className="info-box yellow">
            <p><strong>Common System Burdens Include:</strong></p>
            <p>• Unclear strategic goals or contested business cases</p>
            <p>• Poor portfolio governance and slow decision-making</p>
            <p>• Weak risk management and planning processes</p>
            <p>• Delivery bottlenecks and interdependency tangles</p>
            <p>• Missing performance data or unreliable forecasting</p>
            <p>• Stakeholder disengagement and fragile sponsorship</p>
            <p>• Toxic team dynamics and broken communication</p>
            <p>• Organizational complexity and dependency overload</p>
            <p>• Transformation fatigue from previous failures</p>
            <p>• Talent gaps and critical role instability</p>
          </div>

          <div className="info-box orange">
            <p><strong>Evidence Base:</strong></p>
            <p>McKinsey (2021) research shows that the average transformation loses 43% of its intended value. Of that loss, approximately 68.5% is attributable to system-level issues that can be addressed through improved organizational capability. This diagnostic helps you identify where those losses are hiding and what you can realistically recover.</p>
            <p style={{ marginTop: '8px' }}><em>Source: McKinsey & Company, 2021, "The transformation imperative: Unlocking value"</em></p>
          </div>
        </div>

        {/* What to Expect */}
        <div className="card">
          <h2>What to Expect</h2>
          <p>This assessment takes approximately 15-20 minutes to complete and consists of four steps:</p>

          <div className="expect-grid">
            <div className="expect-item">
              <h3>Step 1: Introduction</h3>
              <p>Understand what system burden is and review the 14 dependencies you'll assess</p>
            </div>

            <div className="expect-item">
              <h3>Step 2: Value at Stake</h3>
              <p>Estimate your annual transformation value to calculate what's at risk from system burdens</p>
            </div>

            <div className="expect-item">
              <h3>Step 3: Rate Dependencies</h3>
              <p>Assess all 14 system dependencies based on capability (0 = critical constraint, 10 = excellence)</p>
            </div>

            <div className="expect-item">
              <h3>Step 4: Your Results</h3>
              <p>Receive your DCF score, value at risk analysis, top constraints, and recommendations</p>
            </div>
          </div>
        </div>

        {/* What You'll Learn */}
        <div className="card">
          <h2>What You'll Learn</h2>

          <div className="info-box blue">
            <p><strong>Your Organizational Capability Profile:</strong></p>
            <p>• Total Value at Risk (based on McKinsey research showing 43% average transformation loss)</p>
            <p>• Recoverable Value (what you can realistically bring back through system improvements)</p>
            <p>• System vs Leadership Loss Breakdown (how much is structural vs psychological)</p>
          </div>

          <div className="info-box yellow">
            <p><strong>Your Critical Constraints:</strong></p>
            <p>• Top 3 dependencies with lowest capability scores</p>
            <p>• Impact analysis for each critical constraint</p>
            <p>• Clear understanding of what's blocking your transformation success</p>
            <p>• Prioritized focus areas for maximum impact</p>
          </div>

          <div className="info-box orange">
            <p><strong>Evidence-Based Interventions:</strong></p>
            <p>• System improvements (process, tools, structure)</p>
            <p>• Leadership development (coaching, capability building)</p>
            <p>• Prioritized action plan based on your specific constraints</p>
            <p>• Clear next steps to start recovering value immediately</p>
          </div>
        </div>

        {/* 14 Dependencies Overview */}
        <div className="card">
          <h2>The 14 System Dependencies We Assess</h2>
          <p>You'll rate each of these dependencies on a 0-10 scale, where 0 means "critical constraint blocking progress" and 10 means "best-in-class enterprise excellence."</p>

          <div className="dependencies-grid">
            <div className="dependency-item">
              <strong>1. Strategic clarity and value case quality</strong>
              <p>Clear transformation goals, compelling business case, measurable outcomes</p>
            </div>

            <div className="dependency-item">
              <strong>2. Portfolio governance and prioritisation</strong>
              <p>Decision rights clarity, effective prioritization, governance that enables not blocks</p>
            </div>

            <div className="dependency-item">
              <strong>3. Risk planning and mitigation</strong>
              <p>Living risk intelligence, adaptive planning, ability to pivot without freezing</p>
            </div>

            <div className="dependency-item">
              <strong>4. Delivery flow and execution rhythm</strong>
              <p>Work prioritization, dependency mapping, bottleneck resolution</p>
            </div>

            <div className="dependency-item">
              <strong>5. Performance management and forecasting</strong>
              <p>Trustworthy data, timely metrics, reliable forecasting for course correction</p>
            </div>

            <div className="dependency-item">
              <strong>6. Change implementation capability</strong>
              <p>BA/DM skills, change translation capability, turning plans into executed change</p>
            </div>

            <div className="dependency-item">
              <strong>7. Stakeholder engagement and network</strong>
              <p>Trust-based relationships, active sponsorship, stakeholder buy-in and support</p>
            </div>

            <div className="dependency-item">
              <strong>8. Leadership and team dynamics</strong>
              <p>Team cohesion, psychological safety, leadership effectiveness</p>
            </div>

            <div className="dependency-item">
              <strong>9. Culture, communication and narrative truth</strong>
              <p>Authentic communication, ability to speak truth, cultural alignment</p>
            </div>

            <div className="dependency-item">
              <strong>10. Hidden work and dependence load</strong>
              <p>Invisible coordination work, key person dependencies, hidden bottlenecks</p>
            </div>

            <div className="dependency-item">
              <strong>11. Organisational complexity and dependency density</strong>
              <p>Structural complexity, tangled dependencies, organizational simplicity</p>
            </div>

            <div className="dependency-item">
              <strong>12. Transformation fatigue and readiness</strong>
              <p>Energy and willingness for change, recovery from past failures, readiness</p>
            </div>

            <div className="dependency-item">
              <strong>13. Meeting architecture and cadence quality</strong>
              <p>Effective meetings, clear cadences, decision-making rhythm</p>
            </div>

            <div className="dependency-item">
              <strong>14. Talent, skills and availability</strong>
              <p>Critical role coverage, skills availability, talent retention and stability</p>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="card">
          <h2>Privacy & Confidentiality</h2>
          <p>Your responses are confidential and used solely to generate your personalized system burden report. No data is shared with third parties. This assessment is organizational and focuses on structural issues, not individual performance or wellbeing.</p>
        </div>

        {/* Persona Selection */}
        <div className="card">
          <h2>Step 1 - Select Your Organizational Profile</h2>
          <p>Choose the profile that best matches your current role and organizational context.</p>

          <div className="persona-grid">
              {/* Mark Ellison Card */}
              <div
                className={`persona-card ${assessmentData.personaId === 'mark' ? 'selected' : ''}`}
                onClick={() => handlePersonaSelect('mark')}
                role="button"
                tabIndex={0}
                aria-label="Select Mark Ellison persona: Transformation Director with 70% organizational capability baseline"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePersonaSelect('mark');
                  }
                }}
              >
                <div className="persona-header">
                  <div className="persona-name">Mark Ellison</div>
                  {assessmentData.personaId === 'mark' && (
                    <div className="persona-badge">SELECTED</div>
                  )}
                </div>
                <div className="persona-role">{PERSONA_BASELINES.mark.role}</div>
                <div className="persona-description">{PERSONA_BASELINES.mark.description}</div>
                <div className="persona-metrics">
                  <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES.mark.strain}</p>
                  <p><strong>Typical Context:</strong> Complex systems, technical debt, integration challenges</p>
                </div>
              </div>

              {/* Sarah Lawson Card */}
              <div
                className={`persona-card ${assessmentData.personaId === 'sarah' ? 'selected' : ''}`}
                onClick={() => handlePersonaSelect('sarah')}
                role="button"
                tabIndex={0}
                aria-label="Select Sarah Lawson persona: Chief Operating Officer with 65% organizational capability baseline"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handlePersonaSelect('sarah');
                  }
                }}
              >
                <div className="persona-header">
                  <div className="persona-name">Sarah Lawson</div>
                  {assessmentData.personaId === 'sarah' && (
                    <div className="persona-badge">SELECTED</div>
                  )}
                </div>
                <div className="persona-role">{PERSONA_BASELINES.sarah.role}</div>
                <div className="persona-description">{PERSONA_BASELINES.sarah.description}</div>
                <div className="persona-metrics">
                  <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES.sarah.strain}</p>
                  <p><strong>Typical Context:</strong> Operational excellence, process improvement, change management</p>
                </div>
              </div>
            </div>
          </div>

        {/* Continue Button */}
        <div className="card" style={{ textAlign: 'center' }}>
          <button
            className="btn-primary"
            onClick={() => {
              setCurrentStep(2);
              window.scrollTo(0, 0);
            }}
            aria-label="Continue to Step 2: Value Assessment"
          >
            Continue to Value Assessment
          </button>
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • System Burden Diagnostic • v7.0.0 • Step 1 of 4</p>
        </div>
        </div>
      </>
    );
  }

  // ============================================
  // STEP 2: Value Estimation
  // ============================================
  if (currentStep === 2) {
    const value = parseFloat(assessmentData.valueAtStake) || 0;
    const isValid = value >= 0.1 && value <= 1000;

    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 2 OF 4</div>
          <h1>Transformation Value at Stake</h1>
          <p className="subtitle">
            Estimate the annual value (in millions) that your transformation aims to capture or protect.
          </p>
          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {assessmentData.personaId === 'mark' ? 'Mark Ellison' : 'Sarah Lawson'} • {PERSONA_BASELINES[assessmentData.personaId].role}</p>
            <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES[assessmentData.personaId].strain}</p>
          </div>
        </div>

        {/* Value Input */}
        <div className="card">
          <h2>Enter Your Transformation Value</h2>

          <div className="value-input-container">
            <label className="value-label" htmlFor="value-at-stake-input">Transformation Value (millions £)</label>
            <input
              type="number"
              id="value-at-stake-input"
              className="value-input"
              placeholder="10.5"
              min="0.1"
              max="1000"
              step="0.1"
              value={assessmentData.valueAtStake}
              onChange={handleValueChange}
              aria-label="Enter transformation value at stake in millions of pounds, between 0.1 and 1000"
              aria-describedby="value-hint"
              aria-required="true"
            />
            <p id="value-hint" className="value-hint">
              Enter a value between £0.1M and £1,000M - this represents the total annual value your transformation is designed to deliver.
            </p>
          </div>

          <div className="examples-box">
            <h3>What to Include in Your Estimate:</h3>
            <ul>
              <li>Revenue growth from new capabilities or market expansion</li>
              <li>Cost savings from process improvements or automation</li>
              <li>Risk mitigation value from modernization initiatives</li>
              <li>Strategic value from improved customer experience</li>
              <li>Operational efficiency gains from digital transformation</li>
              <li>Competitive advantage value from innovation programs</li>
              <li>Compliance and regulatory risk reduction</li>
            </ul>
          </div>

          <div className="info-box blue">
            <p><strong>Research Context:</strong></p>
            <p>Based on McKinsey research (2021), transformations lose an average of 43% of planned value. This assessment helps you understand how much of that loss is recoverable through system improvements and leadership psychological flexibility.</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="card">
          <div className="button-group">
            <button
              className="btn-back"
              onClick={() => {
                setCurrentStep(1);
                window.scrollTo(0, 0);
              }}
              aria-label="Go back to Step 1: Persona Selection"
            >
              Back to Introduction
            </button>
            <button
              className="btn-primary"
              onClick={handleValueContinue}
              disabled={!isValid}
              aria-label="Continue to Step 3: Dependency Assessment"
              aria-disabled={!isValid}
            >
              Continue to System Analysis
            </button>
          </div>
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • System Burden Diagnostic • v7.0.0 • Step 2 of 4</p>
        </div>
        </div>
      </>
    );
  }

  // ============================================
  // STEP 3: Dependency Assessment
  // ============================================
  if (currentStep === 3) {
    // Define 14 dependencies with titles and descriptions from mockup
    const dependencies = [
      {
        number: 1,
        title: 'Strategic clarity and value case quality',
        question: 'If the business case is woolly, contested or unrealistic, recoverability drops. You can\'t reliably recover value if no one agrees what the value is. Strong strategic alignment means your efforts compound instead of dispersing.'
      },
      {
        number: 2,
        title: 'Portfolio governance and decision rights',
        question: 'Even if the case is clear, recoverability suffers when nobody knows who decides what, or when decisions get stuck. Clean governance and decision rights increase the fraction of lost value you can realistically bring back because decisions actually land.'
      },
      {
        number: 3,
        title: 'Risk and adaptive planning maturity',
        question: 'If risk is treated as a register, not a living practice, then the system under-reacts or over-reacts. Strong risk intelligence means the organisation can pivot instead of freeze or thrash — which increases the proportion of value you can recover mid-flight.'
      },
      {
        number: 4,
        title: 'Delivery flow and interdependency management',
        question: 'This is where a huge amount of recoverable value hides. Poor interdependency management means initiatives block each other and delay compounding benefits. When you improve flow, the same portfolio yields more, faster — materially lifting the recoverability factor.'
      },
      {
        number: 5,
        title: 'Operational performance management and data quality',
        question: 'Without trustworthy, timely data, you can\'t tell whether interventions are working, which slows course corrections and undermines confidence. Strong performance management lets you steer toward recoverable value instead of flying blind.'
      },
      {
        number: 6,
        title: 'Change implementation and BA/DM capability',
        question: 'If nobody can translate intent into workable change, the organisation keeps reliving the same patterns. Solid BA/DM and change capability is essential because it turns plans into executed change — directly affecting what proportion of lost value you actually claw back.'
      },
      {
        number: 7,
        title: 'Stakeholder network and sponsorship strength',
        question: 'Without trust and engagement, initiatives stall in hidden resistance. People withhold effort, information and creativity. A strong stakeholder network increases recoverability because people choose to lean in instead of sabotage or withdraw.'
      },
      {
        number: 8,
        title: 'Leadership and team dynamics',
        question: 'If team dynamics are toxic or leaderless, even good ideas suffocate. Strong leadership and team cohesion multiply effort rather than fracture it, which directly increases the percentage of lost value you can realistically recover.'
      },
      {
        number: 9,
        title: 'Culture, communication and narrative truth',
        question: 'When the truth can\'t be spoken, reality gets distorted. Poor communication and narrative truth means you\'re steering by fiction. Clear, authentic communication creates conditions where value can actually be recovered because you\'re working with facts, not fantasies.'
      },
      {
        number: 10,
        title: 'Hidden work and dependence load',
        question: 'Invisible coordination work consumes energy. Key person dependencies create bottlenecks. When hidden work is high, transformation capacity shrinks. Making work visible and reducing dependence load directly increases your capacity to recover value.'
      },
      {
        number: 11,
        title: 'Organisational complexity and dependency density',
        question: 'Complexity and dependency density create drag. The more tangled the org, the lower your recovery potential. Simplifying and untangling structures directly lifts the fraction of lost value you can realistically bring back.'
      },
      {
        number: 12,
        title: 'Transformation fatigue and history',
        question: 'Past failures cast long shadows. If the organisation is exhausted or cynical, recoverability drops because people have stopped believing change is possible. Fresh energy and willingness to try again are preconditions for bringing lost value back.'
      },
      {
        number: 13,
        title: 'Meeting and cadence architecture',
        question: 'Scattered or misaligned meetings waste time, create confusion, and slow decisions. Strong cadence architecture creates rhythm and clarity, lifting recoverability because the system can move faster and with less friction.'
      },
      {
        number: 14,
        title: 'Talent availability and critical role stability',
        question: 'If you\'re bleeding talent or can\'t fill critical roles, your recovery potential is capped. Talent availability and stability directly affect how much lost value you can realistically claw back because you have the people to do the work.'
      }
    ];

    // Helper to get score color class
    const getScoreColorClass = (score) => {
      if (score >= 0 && score <= 2) return 'score-red';
      if (score >= 3 && score <= 4) return 'score-orange';
      if (score >= 5 && score <= 6) return 'score-amber';
      if (score >= 7 && score <= 8) return 'score-green';
      if (score >= 9 && score <= 10) return 'score-blue';
      return 'score-amber';
    };

    return (
      <>
        <DiagnosticHeader />
        <div className="container">
        {/* Header Card */}
        <div className="card">
          <div className="badge">STEP 3 OF 4</div>
          <h1>System Burden Diagnostic</h1>
          <p className="subtitle">Assess organizational dependencies and calculate your system readiness</p>

          <div className="persona-info">
            <p><strong>Selected Profile:</strong> {assessmentData.personaId === 'mark' ? 'Mark Ellison' : 'Sarah Lawson'} • {PERSONA_BASELINES[assessmentData.personaId].role}</p>
            <p><strong>Leadership Strain:</strong> {PERSONA_BASELINES[assessmentData.personaId].strain}</p>
          </div>
        </div>

        {/* Scoring Guide */}
        <div className="scoring-guide">
          <div className="scoring-header">
            <div className="info-icon">i</div>
            <h2>Capability-Based Scoring Guide (0-10 Scale)</h2>
          </div>

          <div className="bands-container">
            <div className="band band-0-2">
              <div className="range">0-2</div>
              <div className="label">Critical constraint</div>
              <div className="description">(blocks progress)</div>
            </div>

            <div className="band band-3-4">
              <div className="range">3-4</div>
              <div className="label">Structural weakness</div>
              <div className="description">(frequent issues)</div>
            </div>

            <div className="band band-5-6">
              <div className="range">5-6</div>
              <div className="label">Working but inconsistent</div>
              <div className="description">(fragile under pressure)</div>
            </div>

            <div className="band band-7-8">
              <div className="range">7-8</div>
              <div className="label">Operational strength</div>
              <div className="description">(reliable)</div>
            </div>

            <div className="band band-9-10">
              <div className="range">9-10</div>
              <div className="label">Enterprise excellence</div>
              <div className="description">(best-in-class)</div>
            </div>
          </div>

          <div className="usage-note">
            <strong>How to use:</strong> Move each slider to indicate how your organization experiences each dependency.
            <span className="red"> 0 = Critical constraint blocking progress</span>,
            <span className="blue"> 10 = Best-in-class enterprise excellence</span>.
          </div>
        </div>

        {/* 14 Dependency Sliders */}
        <div className="card">
          <h2>Rate All 14 System Dependencies</h2>

          {dependencies.map((dep) => {
            const depKey = `dep${dep.number}`;
            const score = assessmentData.scores[depKey];
            const colorClass = getScoreColorClass(score);

            return (
              <div key={depKey} className="dependency-item">
                <div className="dependency-header">
                  <div className="dependency-title">
                    <h3>{dep.number}. {dep.title}</h3>
                    <p className="dependency-question">{dep.question}</p>
                  </div>
                  <div className="dependency-score">
                    <span className={`value ${colorClass}`}>{score}</span>
                    <span className="max">/10</span>
                  </div>
                </div>
                <div className="slider-container">
                  <input
                    type="range"
                    id={`slider-${depKey}`}
                    min="0"
                    max="10"
                    value={score}
                    onChange={(e) => handleScoreChange(depKey, parseInt(e.target.value))}
                    aria-label={`${dep.title}: Rate organizational capability from 0 (critical constraint) to 10 (enterprise excellence)`}
                    aria-valuemin="0"
                    aria-valuemax="10"
                    aria-valuenow={score}
                    aria-valuetext={`${score} out of 10: ${
                      score >= 0 && score <= 2 ? 'Critical constraint' :
                      score >= 3 && score <= 4 ? 'Structural weakness' :
                      score >= 5 && score <= 6 ? 'Working but inconsistent' :
                      score >= 7 && score <= 8 ? 'Operational strength' :
                      'Enterprise excellence'
                    }`}
                  />
                  <div className="slider-labels" role="presentation" aria-hidden="true">
                    <span className="label-red">0-2<br/>Critical</span>
                    <span className="label-orange">3-4<br/>Weak</span>
                    <span className="label-amber">5-6<br/>Moderate</span>
                    <span className="label-green">7-8<br/>Strong</span>
                    <span className="label-blue">9-10<br/>Excellent</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Navigation */}
        <div className="card">
          <div className="button-group">
            <button
              className="btn-back"
              onClick={() => {
                setCurrentStep(2);
                window.scrollTo(0, 0);
              }}
              disabled={loading}
              aria-label="Go back to Step 2: Value Assessment"
              aria-disabled={loading}
            >
              Back to Value at Stake
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={loading}
              aria-label={loading ? 'Submitting assessment data' : 'Generate results and proceed to Step 4'}
              aria-disabled={loading}
            >
              {loading ? 'Submitting...' : 'Generate Results'}
            </button>
          </div>

          {error && (
            <div style={{
              marginTop: '24px',
              padding: '16px',
              background: '#fee2e2',
              border: '2px solid #ef4444',
              borderRadius: '8px',
              color: '#991b1b'
            }}>
              <p style={{ fontWeight: '600' }}>Error: {error}</p>
              <p style={{ fontSize: '13px', marginTop: '8px' }}>
                Please try again. If the problem persists, contact support.
              </p>
            </div>
          )}
        </div>

        {/* Design Note */}
        <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: '14px', marginTop: '32px' }}>
          <p>Daring Futures • System Burden Diagnostic • v7.0.0 • Step 3 of 4</p>
        </div>
        </div>
      </>
    );
  }

  return null;
}

export default SystemBurdenDiagnostic;
